import { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyListOutDTO } from '@api/ringil-api.v2';
import { OptionType } from '@components/RingilDataTable/RingilDataTable.tsx';

const PAGE_SIZE = 50;

const toOption = (company: CompanyListOutDTO | { companyId: string; name: string }): OptionType => ({
  label: company.name,
  value: company.companyId,
  active: true,
});

const withSelected = (options: OptionType[], selected: OptionType | null): OptionType[] =>
  selected && !options.some(o => o.value === selected.value) ? [selected, ...options] : options;

/**
 * Windowed company options for a {@link FilterType.DropdownLazy}. There are ~1.5k companies and no
 * list-of-values endpoint, so the admin grid is paged and its `q` answers the panel's search box.
 *
 * The selected company is kept in the option list even when it falls outside the current window,
 * otherwise the chip shows its raw id — see {@link resolveSelected} for the cold-load path.
 */
export const useLazyCompanies = () => {
  const { adminListCompanies, adminGetCompanyDetail } = useContext(ApiContext);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(false);

  const query = useRef('');
  const page = useRef(0);
  const hasMore = useRef(true);
  const inFlight = useRef(false);
  const loaded = useRef<OptionType[]>([]);
  const selected = useRef<OptionType | null>(null);
  const resolving = useRef<string | null>(null);

  const publish = () => setOptions(withSelected(loaded.current, selected.current));

  const load = (nextPage: number, q: string) => {
    inFlight.current = true;
    setLoading(true);
    adminListCompanies({ pageNumber: nextPage, pageSize: PAGE_SIZE }, q || null, null, data => {
      const batch = (data?.data ?? []).map(toOption);
      const merged = nextPage === 1 ? batch : [...loaded.current, ...batch.filter(o => !loaded.current.some(p => p.value === o.value))];
      loaded.current = merged;
      hasMore.current = (data?.total ?? 0) > nextPage * PAGE_SIZE;
      page.current = nextPage;
      inFlight.current = false;
      setLoading(false);
      publish();
    });
  };

  const onLazyLoad = (event: { first: number; last: number; filter?: string }) => {
    const q = (event.filter ?? '').trim();
    // PrimeReact skips client-side filtering while lazy, so a changed search restarts from page 1.
    if (q !== query.current) {
      query.current = q;
      hasMore.current = true;
      load(1, q);
      return;
    }
    if (inFlight.current || !hasMore.current) return;
    if (page.current === 0 || event.last >= loaded.current.length) load(page.current + 1, q);
  };

  /** Cold load of a persisted filter: the company is selected but was never in a loaded window. */
  const resolveSelected = (companyId?: string) => {
    if (!companyId) {
      if (selected.current) {
        selected.current = null;
        publish();
      }
      return;
    }
    if (selected.current?.value === companyId || resolving.current === companyId) return;
    const known = loaded.current.find(o => o.value === companyId);
    if (known) {
      selected.current = known;
      publish();
      return;
    }
    resolving.current = companyId;
    adminGetCompanyDetail(companyId, detail => {
      resolving.current = null;
      if (!detail) return;
      selected.current = toOption(detail);
      publish();
    });
  };

  return { options, loading, onLazyLoad, resolveSelected };
};
